import React from "react";
import { Badge } from "@/components/ui/badge";
import { useNavigate } from "react-router-dom";
import { useFeatureGating } from "@/contexts/FeatureGatingContext";

interface ProBadgeProps {
  featureName: string;
  children?: React.ReactNode;
  className?: string;
  variant?: "badge" | "overlay" | "inline";
  size?: "sm" | "md" | "lg";
  showUpgradeToast?: boolean;
  onUpgradeClick?: () => void;
}

export const ProBadge: React.FC<ProBadgeProps> = ({
  featureName,
  children,
  className = "",
  variant = "badge",
  size = "sm",
  showUpgradeToast = true,
  onUpgradeClick,
}) => {
  const navigate = useNavigate();

  const handleUpgradeClick = () => {
    if (onUpgradeClick) {
      onUpgradeClick();
    } else if (showUpgradeToast) {
      // For now, just navigate - we'll handle the custom toast in the sidebar
      navigate("/settings?tab=billing");
    } else {
      navigate("/settings?tab=billing");
    }
  };

  const badgeClasses = {
    sm: "text-xs px-2 py-0 h-5",
    md: "text-sm px-3 py-1 h-6",
    lg: "text-base px-4 py-2 h-8",
  };

  const badgeComponent = (
    <Badge
      variant="secondary"
      className={`${badgeClasses[size]} bg-blue-600 text-white cursor-pointer hover:bg-blue-700 transition-colors ${className}`}
      onClick={handleUpgradeClick}
    >
      PRO
    </Badge>
  );

  if (variant === "badge") {
    return badgeComponent;
  }

  if (variant === "overlay") {
    return (
      <div className="relative">
        {children}
        <div className="absolute -top-1 -right-1">{badgeComponent}</div>
      </div>
    );
  }

  if (variant === "inline") {
    return (
      <div className={`flex items-center justify-between ${className}`}>
        {children}
        {badgeComponent}
      </div>
    );
  }

  return badgeComponent;
};

// Hook to check if a feature requires Pro upgrade
export const useProFeature = (featureType: string) => {
  const { canUseFeature } = useFeatureGating();
  const featureAccess = canUseFeature(featureType as any);

  return {
    isAllowed: featureAccess.allowed,
    isPro: !featureAccess.allowed && featureAccess.reason === "not_in_plan",
    upgradeMessage: featureAccess.message,
  };
};
